import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl shadow-pill hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl shadow-pill hover:-translate-y-0.5",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-xl shadow-pill hover:-translate-y-0.5",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-xl shadow-pill hover:-translate-y-0.5",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline rounded-md",
        pill: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 shadow-pill hover:-translate-y-0.5",
        pillOutline: "border-2 border-primary bg-background text-foreground hover:bg-primary hover:text-primary-foreground rounded-full px-6 shadow-pill hover:-translate-y-0.5",
        pillSecondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-6 shadow-pill hover:-translate-y-0.5",
      },
      size: {
        default: "h-10 px-4 py-2 md:h-10",
        sm: "h-11 px-4 md:h-9 md:px-3", // Larger on mobile (44px min)
        lg: "h-12 px-8",
        icon: "h-11 w-11 md:h-10 md:w-10", // Larger on mobile
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
